import { useMemo, useState } from "react";
import { Books, X } from "@phosphor-icons/react";
import { LIFT_TO_1RM, PLYO_IDS, AEROBIC_IDS, ANAEROBIC_IDS } from "../../lib/rescale";

const DOMAINS = ["All", "Strength", "Endurance", "Mobility", "ACFT"];
const QUICK_CHIPS = [
  { id: "no-equipment", label: "No Equip", type: "equipment", value: "None" },
  { id: "barbell", label: "Barbell", type: "equipment", value: "Barbell" },
  { id: "dumbbell", label: "DB", type: "equipment", value: "Dumbbell" },
  { id: "kettlebell", label: "KB", type: "equipment", value: "Kettlebell" },
  { id: "core", label: "Core", type: "muscle", value: "Core" },
  { id: "cardio", label: "Cardio", type: "muscle", value: "Cardio" },
  { id: "power", label: "Power", type: "muscle", value: "Power" },
  { id: "plyo", label: "Plyo", type: "set", value: PLYO_IDS },
  { id: "aerobic", label: "Aerobic", type: "set", value: AEROBIC_IDS },
  { id: "anaerobic", label: "Anaerobic", type: "set", value: ANAEROBIC_IDS },
  { id: "1rm", label: "1RM", type: "set", value: new Set(Object.keys(LIFT_TO_1RM)) },
];

export default function ExercisePicker({ exercises, onPick, onClose }) {
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("All");
  const [chip, setChip] = useState(null);

  const filtered = useMemo(() => {
    return exercises.filter((e) => {
      if (domain !== "All" && e.domain !== domain) return false;
      if (q) {
        const hay = `${e.name} ${e.domain} ${(e.muscle_groups || []).join(" ")}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      if (chip) {
        if (chip.type === "equipment" && e.equipment !== chip.value) return false;
        if (chip.type === "muscle") {
          const hit = (e.muscle_groups || []).some((m) =>
            m.toLowerCase().includes(chip.value.toLowerCase())
          );
          if (!hit) return false;
        }
        if (chip.type === "set" && !chip.value.has(e.id)) return false;
      }
      return true;
    });
  }, [exercises, q, domain, chip]);

  const toggleChip = (c) => setChip((curr) => (curr?.id === c.id ? null : c));

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-card border border-border w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Books size={20} weight="bold" className="text-volt" />
            <h3 className="font-display text-2xl font-black">SELECT EXERCISE</h3>
          </div>
          <button onClick={onClose} data-testid="picker-close"><X size={20} weight="bold" /></button>
        </div>

        <div className="p-4 border-b border-border space-y-3">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name or muscle group..."
            className="w-full bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt"
            data-testid="picker-search"
          />

          <div className="flex flex-wrap gap-1">
            {DOMAINS.map((d) => (
              <button
                key={d}
                onClick={() => setDomain(d)}
                data-testid={`picker-domain-${d}`}
                className={`px-3 py-1.5 text-xs uppercase tracking-widest font-display border ${
                  domain === d ? "bg-volt text-black border-volt" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {d}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1">
            {QUICK_CHIPS.map((c) => {
              const active = chip?.id === c.id;
              return (
                <button
                  key={c.id}
                  onClick={() => toggleChip(c)}
                  data-testid={`picker-chip-${c.id}`}
                  className={`px-2.5 py-1 text-xs uppercase tracking-widest font-display border ${
                    active ? "bg-volt text-black border-volt" : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          <div className="label-mono text-muted-foreground" data-testid="picker-count">
            {filtered.length} / {exercises.length} drills
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {filtered.map((e) => (
            <button key={e.id} onClick={() => onPick(e)} className="text-left flex gap-3 border border-border hover:border-volt p-2" data-testid={`picker-item-${e.id}`}>
              <img src={e.image_url} alt="" className="w-16 h-16 object-cover" loading="lazy" />
              <div>
                <div className="font-display text-sm font-bold uppercase">{e.name}</div>
                <div className="label-mono">{e.domain} · {e.level}</div>
              </div>
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center label-mono text-muted-foreground py-8">
              NO DRILLS MATCH
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
